import { useRef, useEffect, useState } from 'react';
import { Alert, Box, LinearProgress, useMediaQuery, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';

const SILENCE_THRESHOLD = 0.1; // порог "тишины" (volume 0..1), при речи volume обычно > 0.1
const SILENCE_ALERT_DELAY_MS = 5000;
const SILENCE_CHECK_INTERVAL_MS = 100;

interface VoiceActivityDetectorProps {
  volume?: number; // 0..1
  isRecording: boolean;
  hideIcon?: boolean;
}

export const VoiceActivityDetector = ({
  volume = 0,
  isRecording,
  hideIcon = false
}: VoiceActivityDetectorProps) => {
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const iconSize = upMd ? 32 : upSm ? 28 : 24;

  const [showSilenceAlert, setShowSilenceAlert] = useState(false);
  const volumeRef = useRef(volume);
  const silenceIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const silenceStartTimeRef = useRef<number | null>(null);

  volumeRef.current = volume;

  // volume нормализуем до 0–100
  const vol = Math.max(0, Math.min(volume * 300, 100));

  useEffect(() => {
    if (!isRecording) {
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      silenceStartTimeRef.current = null;
      setShowSilenceAlert(false);
      return;
    }

    silenceIntervalRef.current = setInterval(() => {
      const currentVolume = volumeRef.current;
      const isSilent = currentVolume < SILENCE_THRESHOLD;

      if (!isSilent) {
        silenceStartTimeRef.current = null;
        setShowSilenceAlert(false);
        return;
      }

      const now = Date.now();
      if (silenceStartTimeRef.current === null) {
        silenceStartTimeRef.current = now;
      }
      const elapsed = now - silenceStartTimeRef.current;
      if (elapsed >= SILENCE_ALERT_DELAY_MS) {
        setShowSilenceAlert(true);
      }
    }, SILENCE_CHECK_INTERVAL_MS);

    return () => {
      if (silenceIntervalRef.current) {
        clearInterval(silenceIntervalRef.current);
        silenceIntervalRef.current = null;
      }
      silenceStartTimeRef.current = null;
    };
  }, [isRecording]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        maxWidth: showSilenceAlert ? 180 : 100
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {!hideIcon && !showSilenceAlert ? (
          <Icon
            icon={isRecording ? 'mdi:microphone' : 'mdi:microphone-off'}
            width={iconSize}
            height={iconSize}
            color={isRecording ? theme.palette.success.main : theme.palette.grey[500]}
          />
        ) : null}
        {!showSilenceAlert ? (
          <LinearProgress
            variant="determinate"
            value={vol > 15 ? vol : 0}
            sx={{
              flexGrow: 1,
              height: 8,
              borderRadius: 4,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#4caf50'
              }
            }}
          />
        ) : (
          <Alert
            severity="warning"
            sx={{
              alignItems: 'center',
              py: 0,
              height: 28,
              '& .MuiAlert-message': { fontSize: '0.875rem', p: 0 },
              '& .MuiAlert-icon': { p: 0 },
              fontSize: 10
            }}
          >
            <span style={{ fontSize: 10 }}>Не слышно</span>
          </Alert>
        )}
      </Box>
    </Box>
  );
};
